import { inject } from "@angular/core";
import { CanActivateChildFn, CanActivateFn, Router } from "@angular/router";
import { AuthService } from "../auth/interceptors/auth.service";
import { AuthUtils } from "../auth/interceptors/auth.utils";
import { MsalBroadcastService } from "@azure/msal-angular";
import { InteractionStatus } from "@azure/msal-browser";
import { UserService } from "app/services/user.service";
import { filter, interval, map, of, switchMap, take } from "rxjs";

export const NoAuthGuard: CanActivateFn | CanActivateChildFn = (
  route,
  state,
) => {
  const router: Router = inject(Router);
  const msalBroadcastService = inject(MsalBroadcastService);
  const userService = inject(UserService);

  const hasMsalHash =
    window.location.hash.includes("code=") ||
    window.location.hash.includes("id_token=") ||
    window.location.hash.includes("error=");

  // Standard check: if AEM token is valid, user is already logged in → send to home
  const checkNoAuth$ = inject(AuthService)
    .check()
    .pipe(
      switchMap((authenticated) => {
        if (authenticated) {
          return of(router.createUrlTree(["/home"]));
        }
        return of(true);
      }),
    );

  // On mobile, MSAL redirect lands back on /login with a hash.
  // AppComponent.handleRedirectObservable() → getToken() → signIn() runs in the
  // background. We must NOT let NoAuthGuard block the login page during this window.
  // Instead: wait for MSAL to finish, then poll for the AEM token.
  if (hasMsalHash) {
    return msalBroadcastService.inProgress$.pipe(
      filter((status) => status === InteractionStatus.None),
      take(1),
      switchMap(() =>
        interval(100).pipe(
          map(() => {
            const token = localStorage.getItem("aemAccessToken");
            return {
              hasValidToken: !!token && !AuthUtils.isTokenExpired(token),
              isLoading: userService.isLoading(),
            };
          }),
          // Resolve once token is ready OR AppComponent has finished loading
          filter((s) => s.hasValidToken || !s.isLoading),
          take(1),
          switchMap((s) =>
            // Token is ready → redirect to home (AppComponent also does router.navigate('/'))
            // No token → allow login page (signIn failed or was skipped)
            s.hasValidToken ? of(router.createUrlTree(["/home"])) : of(true),
          ),
        ),
      ),
    );
  }

  return checkNoAuth$;
};
